import React, { FC, forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import { useNavigate } from 'react-router';
import { isSafari, isIphone } from 'utils/fnDevices';
import icons from 'style';
import spinner from 'assets/images/spinner.png';
import { VolumeControl, VolumeControlElements, updateVolumeBarValueText } from 'components/VolumeControl/VolumeControl';
import { SeekBar, updateSeekBarValueText } from 'components/SeekBar/SeekBar';
import { PLAYER_EPISODES_DROPDOWN_EVENT } from 'components/DropdownMenu/DropdownMenu';
import { useKeyboardShortcuts } from 'hooks/useKeyboardShortcuts/useKeyboardShortcuts';
import { useConfig } from 'providers/useConfig/ConfigContext';
import { usePlayer } from 'providers/player/PlayerContext';
import { buildTimeString } from 'utils/fnDate';
import translate from 'utils/fnTranslate';
import {
    LivePlayerAssetInfo,
    PlayerControl,
    PlayerEvents,
    PlayerKeyboardControl,
    PlayingAssetType,
    PlayingMode,
    PropTypesAdditional,
    PropTypesPlayerUICommon,
} from 'types/Player';
import { ControlGroup } from 'components/Player/PlayerWrapper';
import { getLocalStorage, guardedSessionStorage } from 'utils/fnStorage';
import { useApp } from 'providers/useApp/AppContext';
import { useCast } from 'providers/cast/CastContext';
import { getLivePlayerURL } from 'utils/fnUrl';
import { isOrientationLandscape } from 'style/styled-components/cssMediaQueries';
import { DetailMoreModal } from 'components/DetailMoreModal/DetailMoreModal';
import { PersonRole } from 'types/Asset';
import KeyboardShortcutOverlay from 'components/KeyboardShortcutOverlay/KeyboardShortcutOverlay';
import { isLivePlayerAsset } from 'utils/fnTypeGuards';
import { usePlayerTabNavigation } from 'hooks/usePlayerTabNavigation/usePlayerTabNavigation';
import { usePlayerSeriesEpisodes } from 'providers/usePlayerSeriesEpisodes/PlayerSeriesEpisodesContext';
import { Picture } from '../Picture/Picture';
import { PageHeading } from '../PageHeading/PageHeading.css';
import {
    MinimizeButton,
    CastingLabel,
    CloseButton,
    CloseExtraControls,
    ControlButtonLabel,
    ControlButtonWrapper,
    FooterButtonsContainer,
    FooterButtonsGroup,
    HeaderControls,
    ExtraControlsWrapper,
    FooterControls,
    FullScreenToggleIcon,
    JumpButtonIcon,
    JumpButtonLabel,
    JumpButtonWrapper,
    LabelMetaData,
    LoadingIndicatorWrapper,
    MetaDataContainer,
    MetaDataTextColumn,
    MiddleButtonsContainer,
    PlayerButton,
    PlayerControls,
    PlayerMetaData,
    SeekBarHolder,
    StyledSVGInline,
    Title,
    VolumeControlButton,
    VolumeControlHoveringWrapper,
    HeaderControlsInnerContainer,
    HideControlsButton,
    HeaderEmbeddedControls,
    EmbeddedMetaDataContainer,
    EmbeddedTitle,
    EmbeddedChanelInfoContainer,
    EmbeddedMiddleButtonsContainer,
    VolumeEmbeddedWrapper,
    VolumeControlEmbedded,
    VolumeEmbeddedContainer,
    ButtonsInnerContainer,
    ChanelInfoContainer,
    Subtitle,
    MetadataSubtitle,
    MetaDataContainerMobile,
    MoreInfoArrow,
    FullScreenContainer,
    UpperContainer,
    ChannelLogo,
} from './PlayerUI.css';

const PROGRESS_UPDATE_INTERVAL = 350;
const SKIP_JUMP_VALUE = 10;
const JUMP_DEBOUNCE_DELAY = 500;
const JUMP_RESET_DELAY = 1000;
const DOUBLE_CLICK_DELAY = 250;
const THUMBNAIL_WIDTH = 300;
export const PLAYER_SETTINGS_DROPDOWN_EVENT = 'player-settings-trigger';

interface ControlButtonProps {
    label?: string;
    render?: boolean;
    className?: string;
    onClick?: () => void;
    isEnabled: boolean;
    invisible?: boolean;
    asset?: any;
    controlType?: PlayerControl;
    children?: React.ReactNode;
    tabIndex?: number;
    expanded?: boolean;
    pressed?: boolean;
    asContainer?: boolean;
}

interface JumpButtonProps {
    jumpValue: number;
    icon: React.ReactNode;
    isForward: boolean;
    totalJumpValue: number;
}

// eslint-disable-next-line react/display-name
export const ControlButton = forwardRef<HTMLButtonElement, ControlButtonProps & { isHeaderControl?: boolean }>(
    (
        {
            label,
            render = true,
            invisible = false,
            className,
            isEnabled,
            onClick,
            children,
            isHeaderControl = false,
            expanded,
            pressed,
            asContainer = false,
        },
        ref
    ) => {
        const handleClick = useCallback(() => {
            if (isEnabled && onClick) {
                onClick();
            }
        }, [isEnabled, onClick]);

        if (!render) {
            return null;
        }

        if (asContainer) {
            return (
                <ControlButtonWrapper
                    as="div"
                    className={className}
                    $disabled={!isEnabled}
                    invisible={invisible}
                    ref={ref}
                    inert={!isEnabled || undefined}
                    isHeaderControl={isHeaderControl}
                >
                    {children}
                    {label != null && (
                        <ControlButtonLabel data-test-id={`player-control-button-${label}`} aria-hidden>
                            {label}
                        </ControlButtonLabel>
                    )}
                </ControlButtonWrapper>
            );
        }

        return (
            <ControlButtonWrapper
                as={onClick ? 'button' : 'div'}
                type={onClick ? 'button' : undefined}
                className={className}
                onClick={handleClick}
                $disabled={!isEnabled}
                invisible={invisible}
                ref={ref}
                tabIndex={isEnabled ? 0 : -1}
                aria-label={label}
                aria-disabled={!isEnabled || undefined}
                aria-expanded={expanded}
                aria-pressed={pressed}
                role={onClick ? undefined : 'button'}
                isHeaderControl={isHeaderControl}
            >
                {children}
                {label != null && <ControlButtonLabel data-test-id={`player-control-button-${label}`}>{label}</ControlButtonLabel>}
            </ControlButtonWrapper>
        );
    }
);

const JumpButton: React.FC<JumpButtonProps> = ({ jumpValue, icon, totalJumpValue }) => {
    const displayValue = Math.abs(totalJumpValue) === 0 ? jumpValue : Math.abs(totalJumpValue);

    return (
        <JumpButtonWrapper>
            <JumpButtonIcon>{icon}</JumpButtonIcon>
            <JumpButtonLabel>{Math.round(displayValue)}</JumpButtonLabel>
        </JumpButtonWrapper>
    );
};

const ARROW_KEYS = [
    PlayerKeyboardControl.LEFTARROW,
    PlayerKeyboardControl.RIGHTARROW,
    PlayerKeyboardControl.UPARROW,
    PlayerKeyboardControl.DOWNARROW,
];

const ARROW_KEY_WIDGETS =
    'input[type="range"], [role="slider"], [role="radiogroup"], [role="menu"], [role="menubar"], [role="listbox"], [role="grid"], [role="tablist"], [role="tree"]';

const SPACE_ACTIVATED =
    'button, a[href], summary, input[type="checkbox"], input[type="radio"], [role="button"], [role="link"], [role="checkbox"], [role="switch"], [role="radio"], [role="menuitem"], [role="option"], [role="tab"]';

const NON_TEXT_INPUT_TYPES = ['range', 'button', 'checkbox', 'radio', 'submit', 'reset', 'image', 'file', 'color'];

const isTextEntry = (element: Element) =>
    element.matches('textarea, select, [contenteditable]:not([contenteditable="false"])') ||
    (element instanceof HTMLInputElement && !NON_TEXT_INPUT_TYPES.includes(element.type));

const BufferingIndicator: FC<{ buffering: boolean }> = ({ buffering }) => {
    if (!buffering) {
        return null;
    }

    return (
        <LoadingIndicatorWrapper>
            <img src={spinner} alt="" />
        </LoadingIndicatorWrapper>
    );
};

export const PlayerUI: FC<PropTypesPlayerUICommon & PropTypesAdditional> = ({
    shouldHideLayout,
    isFullScreen,
    onControlsRef,
    onBackClicked,
    toggleFullScreen,
    forceExcludedControls = [],
    extraControls,
    loading,
    metaData,
    logo,
    controls,
    disabledControls,
    title,
    subtitle,
    seekEnabled,
    actionPopup,
    mobileOverlayActionHandlers,
    isEmbedded,
    description,
    moreOverlayMetaData,
    cast,
    originalLanguages,
    captionLanguages,
    holdLayout,
    hideLayoutNow,
}) => {
    const {
        isPlaying,
        volume,
        isMuted,
        isMutedRef,
        isBuffering,
        playingMode,
        setPlayingState,
        setVolumeLevel,
        adjustVolume,
        seek,
        seekStart,
        seekEnd,
        getThumbnail,
        showThumbnail,
        jump,
        getProgressWithJump,
        getSecondaryProgressWithJump,
        getProgress,
        getDuration,
        getPrePadding,
        getPostPadding,
        getSeekMax,
        canUpdateProgress,
        activeControlGroup,
        setActiveControlGroup,
        toggleMute,
        jumpToLive,
        playerError,
        loaded,
        setStreamEndReached,
        setIsEmbedded,
        switchToMiniPlayer,
        asset,
        nextEpisode,
    } = usePlayer();

    const navigate = useNavigate();
    const { nextEpisode: nextSeriesEpisode, playEpisode: playSeriesEpisode } = usePlayerSeriesEpisodes();

    const { castConnected, toggleConnect, disconnect, deviceName } = useCast();

    const volumeBar = useRef(null);
    const volumeValue = useRef(null);
    const { config } = useConfig();

    const scrubber = useRef(null);
    const currentTime = useRef(null);
    const remainingTime = useRef(null);
    const progressLine = useRef(null);
    const bufferLine = useRef(null);
    const hoverLine = useRef(null);
    const isLoaded = useRef(false);
    const scrubberValue = useRef(null);
    const thumbnailImage = useRef(null);
    const thumbnail = useRef(null);
    const availableThumbnail = useRef(null);
    const playing = useRef(isPlaying);
    const nextEpisodeRef = useRef(nextEpisode);

    const fullScreenContainerRef = useRef<HTMLDivElement>(null);

    const [isScrubberValueShown, setIsScrubberValueShown] = useState<boolean>(false);
    const [isVolumeHovered, setIsVolumeHovered] = useState<boolean>(false);

    const [isOverlayActive, setIsOverlayActive] = useState(false);
    const [isSeeking, setIsSeeking] = useState(false);
    const isSeekingRef = useRef(false);

    const [overlayIcon, setOverlayIcon] = useState('');
    const [overlayTrigger, setOverlayTrigger] = useState(0);

    const progressUpdateIntervalId = useRef(null);
    const extraControlsLeaveTimeout = useRef(null);
    const surfaceClickTimeout = useRef<number>(null);

    const [forwardJumpValue, setForwardJumpValue] = useState(SKIP_JUMP_VALUE);
    const [backwardJumpValue, setBackwardJumpValue] = useState(SKIP_JUMP_VALUE);
    const currentForwardJumpDistanceRef = useRef(SKIP_JUMP_VALUE);
    const currentBackwardJumpDistanceRef = useRef(SKIP_JUMP_VALUE);
    const lastJumpTimeRef = useRef(0);
    const timeoutRefs = useRef<number[]>([]);
    const [totalJumpValue] = useState(0);

    const isMobileDevice = getLocalStorage('isMobile');
    const { isMobile, isMobileLandscape, isTablet, isDesktop } = useApp();

    const displayTitle = title ?? asset?.title;
    const isPlayerReady = !loading && loaded && !playerError;
    const headingRef = useRef<HTMLHeadingElement>(null);
    const headingFocusedRef = useRef(false);

    useEffect(() => {
        if (isEmbedded || !displayTitle || headingFocusedRef.current || !headingRef.current) return;

        headingFocusedRef.current = true;

        if (!document.activeElement || document.activeElement === document.body) {
            headingRef.current.focus();
        }
    }, [displayTitle, isEmbedded]);

    const displayLogo =
        logo ??
        (asset?.channelLogo && (
            <ChannelLogo>
                <Picture
                    src={asset.channelLogo}
                    alt={(asset as { channelName?: string }).channelName ?? ''}
                    hPosition={'left'}
                    vPosition={'center'}
                    fit={'scale-down'}
                />
            </ChannelLogo>
        ));

    const triggerOverlay = useCallback((icon: string) => {
        setOverlayIcon(icon);
        setOverlayTrigger(Date.now());
    }, []);

    const openOverlay = () => {
        document.dispatchEvent(new CustomEvent(PlayerEvents.INFO_REQUESTED));
        setIsOverlayActive(true);
    };

    const closeOverlay = () => {
        setIsOverlayActive(false);
    };

    const onPlayPauseClicked = async () => {
        triggerOverlay(isPlaying ? 'play' : 'pause');
        await setPlayingState(!isPlaying);
    };

    const clearSurfaceClickTimeout = () => {
        if (surfaceClickTimeout.current !== null) {
            clearTimeout(surfaceClickTimeout.current);
            surfaceClickTimeout.current = null;
        }
    };

    const onPlayerSurfaceClicked = (event: React.MouseEvent) => {
        const canTogglePlayback =
            !playerError && controls.includes(PlayerControl.PLAY_PAUSE) && !disabledControls.includes(PlayerControl.PLAY_PAUSE);

        if (event.detail > 1 || !canTogglePlayback) {
            return;
        }

        if (isEmbedded) {
            onPlayPauseClicked();
            return;
        }

        clearSurfaceClickTimeout();
        surfaceClickTimeout.current = window.setTimeout(() => {
            surfaceClickTimeout.current = null;
            onPlayPauseClicked();
        }, DOUBLE_CLICK_DELAY);
    };

    const onPlayerSurfaceDoubleClicked = () => {
        clearSurfaceClickTimeout();
        toggleFullScreen();
    };

    const onJumpToLiveClicked = async () => {
        triggerOverlay('goToLive');
        await jumpToLive();
    };

    const onVolumeClicked = () => {
        toggleMute();
    };

    const onVolumeButtonClicked = () => {
        onVolumeClicked();
    };

    const onFullScreenToggleClicked = () => {
        toggleFullScreen();
    };

    const onPlayerCloseClicked = (exitPlayer?: boolean) => {
        if (exitPlayer) {
            setStreamEndReached(true);

            // Disconnect from cast if currently casting
            if (castConnected) {
                disconnect().catch(() => {});
            }
        }

        onBackClicked();
    };

    const onVolumeInput = (e) => {
        updateVolumeBarValueText(e.target, parseFloat(e.target.value));
        setVolumeLevel(parseFloat(e.target.value));
    };

    const onVolumeBarCreated = (volumeControlElements: VolumeControlElements) => {
        volumeBar.current = volumeControlElements.volumeBar;
        volumeValue.current = volumeControlElements.volumeValue;
    };

    const onCastClicked = () => {
        toggleConnect().catch(() => {});
    };

    const toggleRefDisplay = (ref: any, display: boolean, callback?: () => void) => {
        if (ref?.current?.style) {
            if (!display) {
                ref.current.style.display = 'none';
            } else {
                ref.current.style.display = 'block';
            }
            if (callback) {
                callback();
            }
        }
        return null;
    };

    const controlsContainerRef = useRef<HTMLDivElement>(null);
    const [isHidingViaButton, setIsHidingViaButton] = useState(false);
    const { setControlRef, focusControl } = usePlayerTabNavigation({
        containerRef: controlsContainerRef,
        holdLayout,
        onVolumeButtonClicked,
    });

    const calculateThumbnailPosition = (cursorX, scrubberWidth, thumbnailCenterOffset, thumbnailWidth) => {
        let position = cursorX - thumbnailCenterOffset;
        position = Math.max(position, 0);
        position = Math.min(position, scrubberWidth - thumbnailWidth);

        return position;
    };

    const setScrubValueAndThumbnailPosition = (playHeadPosition: number, scrubberMiddlePosition: number) => {
        const scrubberWidth = scrubber.current.offsetWidth;
        const scrubberProgress = playHeadPosition * scrubberWidth;

        if (availableThumbnail.current) {
            if (thumbnail.current?.style) {
                const thumbnailWidth = thumbnail.current.offsetWidth;
                const thumbnailPosition = calculateThumbnailPosition(
                    scrubberProgress + 20 - scrubberMiddlePosition,
                    scrubberWidth,
                    thumbnailWidth / 2,
                    thumbnailWidth
                );

                thumbnail.current.style.left = `${thumbnailPosition}px`;

                if (scrubberValue.current?.style) {
                    scrubberValue.current.style.left = `${thumbnailPosition + thumbnailWidth / 2}px`;
                }
            }
        } else {
            scrubberValue.current.style.left = `${scrubberProgress - scrubberMiddlePosition}px`;
        }
    };

    const setThumbnail = (thumb: any) => {
        const scale = thumb.width ? THUMBNAIL_WIDTH / thumb.width : 1;
        const thumbnailHeight = Math.round(thumb.height * scale);

        if (thumbnail.current?.style) {
            thumbnail.current.style.width = `${THUMBNAIL_WIDTH}px`;
            thumbnail.current.style.height = `${thumbnailHeight}px`;
            thumbnail.current.style.top = `calc(-${thumbnailHeight}px - 40px)`;
        }
        if (thumbnailImage.current?.style) {
            const [url] = thumb.uris;
            thumbnailImage.current.src = url;
            thumbnailImage.current.style.transform = `scale(${scale}) translate(-${thumb?.positionX}px, -${thumb?.positionY}px)`;
        }
    };

    const getDataForThumbnail = async (seekValue: number) => {
        if (!availableThumbnail.current) {
            return;
        }
        try {
            const thumb = await getThumbnail(seekValue);
            setThumbnail(thumb);
        } catch {
            toggleRefDisplay(thumbnail, false);
        }
    };

    const getSeekTimeLabel = (value: number) => {
        const streamDuration = getDuration();

        if (streamDuration === 0) {
            return '00:00:00';
        }

        const paddingValue = -(streamDuration - value) + getPostPadding();

        return paddingValue > 1 ? `+${buildTimeString(paddingValue, true)}` : buildTimeString(value - getPrePadding(), true);
    };

    const setScrubberValue = () => {
        const streamDuration = getDuration();
        const seekValue = scrubber.current.value >= streamDuration ? streamDuration - 1 : scrubber.current.value;
        const playHeadPosition = seekValue / streamDuration || 0;
        const scrubberMiddlePosition = playHeadPosition * 20 + 10;

        updateSeekBarValueText(
            scrubber.current,
            Math.max(0, parseFloat(scrubber.current.value) - getPrePadding()),
            streamDuration - getPrePadding() - getPostPadding()
        );

        if (scrubberValue.current?.style) {
            scrubberValue.current.innerHTML = getSeekTimeLabel(parseFloat(scrubber.current.value));

            getDataForThumbnail(parseFloat(seekValue));
            setScrubValueAndThumbnailPosition(playHeadPosition, scrubberMiddlePosition);
        }
        return null;
    };

    const updateTimeAndSeekRange = (force = false) => {
        if (!isLoaded.current || (!playing.current && !force) || isSeekingRef.current) {
            return;
        }

        const duration = getDuration();

        if (duration === 0) {
            return;
        }

        if (isSafari() && isLivePlayerAsset(asset) && !isPlaying) return;

        if (canUpdateProgress()) {
            const elapsedTime = getProgressWithJump();

            if (playingMode === PlayingMode.TIME_SHIFT && elapsedTime === 0) {
                return;
            }

            let playedTimeInSeconds = elapsedTime - getPrePadding();
            let remainingTimeInSeconds = -(duration - elapsedTime) + getPostPadding();

            // When in post padding the played time shout not be counting
            playedTimeInSeconds -= remainingTimeInSeconds > 0 ? remainingTimeInSeconds : 0;
            // When in pre padding the remaining time should not count down
            remainingTimeInSeconds -= playedTimeInSeconds < 0 ? playedTimeInSeconds : 0;

            playedTimeInSeconds = playedTimeInSeconds < -getPrePadding() ? 0 : playedTimeInSeconds;
            remainingTimeInSeconds = remainingTimeInSeconds > getPostPadding() ? 0 : remainingTimeInSeconds;

            if (currentTime.current) {
                currentTime.current.innerHTML =
                    Number.isNaN(elapsedTime) || duration === 0 ? '0:00:00' : buildTimeString(playedTimeInSeconds, true);
            }

            if (remainingTime.current) {
                if (duration === 0) {
                    remainingTime.current.innerHTML = '00:00:00';
                } else {
                    remainingTime.current.innerHTML = `${remainingTimeInSeconds > 1 ? '+' : ''}${
                        Number.isNaN(remainingTimeInSeconds) ? '0:00:00' : buildTimeString(remainingTimeInSeconds, true)
                    }`;
                }
            }

            if (scrubber.current) {
                scrubber.current.min = 0;
                scrubber.current.max = duration ?? 1;
                scrubber.current.value = elapsedTime;
                updateSeekBarValueText(scrubber.current, playedTimeInSeconds, duration - getPrePadding() - getPostPadding());
            }

            const playHeadPosition = elapsedTime / duration || 0;
            if (progressLine.current?.style) {
                progressLine.current.style.width = `${playHeadPosition * 100}%`;
            }
        }

        const secondaryProgress = getSecondaryProgressWithJump() / duration || 0;
        if (bufferLine.current?.style) {
            bufferLine.current.style.width = `${secondaryProgress * 100}%`;
        }
    };

    const updateTimeAndSeekRangeRef = useRef(updateTimeAndSeekRange);
    updateTimeAndSeekRangeRef.current = updateTimeAndSeekRange;

    const onSeekInput = useCallback(async () => {
        const seekMax = getSeekMax();
        const streamDuration = getDuration();

        if (scrubber.current.value > seekMax && seekMax > 0) {
            scrubber.current.value = seekMax;
        }

        const seekValue = scrubber.current.value >= streamDuration ? streamDuration - 1 : scrubber.current.value;
        const playHeadPosition = seekValue / streamDuration || 0;

        if (progressLine.current?.style) {
            progressLine.current.style.width = `${playHeadPosition * 100}%`;
        }

        setScrubberValue();
        await seek(parseFloat(seekValue));
    }, [seek]);

    const onSeekStart = () => {
        if (progressUpdateIntervalId.current) {
            clearInterval(progressUpdateIntervalId.current);
        }
        if (availableThumbnail.current) {
            toggleRefDisplay(thumbnail, true);
        }
        setScrubberValue();
        toggleRefDisplay(scrubberValue, true, () => {
            setIsScrubberValueShown(true);
        });
        isSeekingRef.current = true;
        setIsSeeking(true);
        seekStart();
    };

    const onSeekEnd = () => {
        if (progressUpdateIntervalId.current) {
            clearInterval(progressUpdateIntervalId.current);
        }
        if (availableThumbnail.current) {
            toggleRefDisplay(thumbnail, false);
        }

        isSeekingRef.current = false;
        progressUpdateIntervalId.current = setInterval(updateTimeAndSeekRange, PROGRESS_UPDATE_INTERVAL);
        toggleRefDisplay(scrubberValue, false, () => {
            setIsScrubberValueShown(false);
        });
        setIsSeeking(false);
        seekEnd();
    };

    const onSeekBarCreated = (ref) => {
        scrubber.current = ref.scrubber;
        currentTime.current = ref.currentTime;
        remainingTime.current = ref.remainingTime;
        progressLine.current = ref.progressLine;
        hoverLine.current = ref.hoverLine;
        bufferLine.current = ref.bufferLine;
        scrubberValue.current = ref.scrubberValue;
        thumbnail.current = ref.thumbnailContainer;
        thumbnailImage.current = ref.thumbnailImage;
    };

    const getMetaData = (meta: any) => {
        if (meta?.length) {
            return meta.flatMap((metaItem: string, metaIndex: number) =>
                // some meta arrays included "," and other weird strings that should not be added (e.g John Wick 4 trailer)
                metaItem.split(',').map((item: string, itemIndex: number) => {
                    item = item.trim();
                    if (item.length > 1) {
                        return (
                            <React.Fragment key={`meta-${metaIndex}-${itemIndex}`}>
                                <LabelMetaData data-test-id={'player-content-subtitle'} key={`meta-data-${metaIndex}-${itemIndex}`}>
                                    {item}
                                </LabelMetaData>
                            </React.Fragment>
                        );
                    }
                    return null;
                })
            );
        }

        return null;
    };

    const getMaxJumpValue = (): number => {
        const theshHoldSeconds = (config.app_config.player_settings.binge_watching_threshold / 100) * getDuration();

        if (getSeekMax() === 0) return Infinity;

        if (nextEpisodeRef.current != null) {
            return Math.round(getSeekMax() - getProgress() - theshHoldSeconds);
        }

        return Math.round(getSeekMax() - getProgress());
    };

    const getVolumeIcon = useCallback(() => {
        if (volume === 0 || isMuted) {
            return icons.volumeMutedIcon;
        }

        if (volume <= 0.5 && volume > 0) {
            return icons.volumeMinIcon;
        }

        return icons.volumeIcon;
    }, [isMuted, volume]);

    useEffect(() => {
        nextEpisodeRef.current = nextEpisode;
    }, [nextEpisode]);

    const clearTimeouts = () => timeoutRefs.current.forEach(clearTimeout);

    const handleJump = useCallback(
        (direction: 'forward' | 'backward') => {
            const now = Date.now();
            const timeSinceLastJump = now - lastJumpTimeRef.current;
            const isForward = direction === 'forward';
            const currentProgress = getProgress();
            const maxJump = getMaxJumpValue();

            clearTimeouts();

            let newJumpValue;

            const currentJumpDistanceRef = isForward ? currentForwardJumpDistanceRef : currentBackwardJumpDistanceRef;
            const setJumpValue = isForward ? setForwardJumpValue : setBackwardJumpValue;

            if (timeSinceLastJump < JUMP_DEBOUNCE_DELAY) {
                newJumpValue = currentJumpDistanceRef.current + (isForward ? SKIP_JUMP_VALUE : -SKIP_JUMP_VALUE);
            } else {
                newJumpValue = isForward ? SKIP_JUMP_VALUE : -SKIP_JUMP_VALUE;
            }

            if (isForward) {
                newJumpValue = Math.min(newJumpValue, maxJump);
            } else {
                newJumpValue = Math.max(newJumpValue, -currentProgress);
            }

            currentJumpDistanceRef.current = newJumpValue;
            setJumpValue(Math.round(Math.abs(newJumpValue)));
            lastJumpTimeRef.current = now;

            triggerOverlay(isForward ? 'jumpFw' : 'jumpBw');

            timeoutRefs.current.push(
                window.setTimeout(() => {
                    jump(newJumpValue).then(() => updateTimeAndSeekRangeRef.current(true));
                }, JUMP_DEBOUNCE_DELAY),
                window.setTimeout(() => {
                    setJumpValue(SKIP_JUMP_VALUE);
                    currentJumpDistanceRef.current = SKIP_JUMP_VALUE;
                }, JUMP_RESET_DELAY)
            );
        },
        [jump, triggerOverlay, getMaxJumpValue, getProgress]
    );

    useEffect(() => () => clearTimeouts(), []);

    const centerControls = () => {
        if (isScrubberValueShown) {
            return null;
        }
        return (
            <>
                <ControlButton
                    key={PlayerControl.JUMP_BACK}
                    render={controls.includes(PlayerControl.JUMP_BACK)}
                    isEnabled={!disabledControls.includes(PlayerControl.JUMP_BACK)}
                    label={translate('SCREEN_PLAYER_BWD10_BUTTON')}
                    ref={setControlRef(PlayerControl.JUMP_BACK)}
                    onClick={() => handleJump('backward')}
                >
                    <PlayerButton data-test-id={'player-skip-back-button'}>
                        <JumpButton
                            jumpValue={backwardJumpValue}
                            icon={<StyledSVGInline svg={icons.iconJumpBackwardStrong} />}
                            isForward={false}
                            totalJumpValue={totalJumpValue}
                        />
                    </PlayerButton>
                </ControlButton>
                <ControlButton
                    key={PlayerControl.PLAY_PAUSE}
                    label={translate(isPlaying ? 'SCREEN_PLAYER_PAUSE_BUTTON' : 'SCREEN_PLAYER_PLAY_BUTTON')}
                    invisible={!controls.includes(PlayerControl.PLAY_PAUSE)}
                    isEnabled={!disabledControls.includes(PlayerControl.PLAY_PAUSE)}
                    onClick={onPlayPauseClicked}
                >
                    <PlayerButton>
                        <StyledSVGInline svg={isPlaying ? icons.pauseIconSimple : icons.playIconSimple} />
                    </PlayerButton>
                </ControlButton>
                <ControlButton
                    key={PlayerControl.JUMP_FORWARD}
                    render={controls.includes(PlayerControl.JUMP_FORWARD)}
                    isEnabled={!disabledControls.includes(PlayerControl.JUMP_FORWARD)}
                    label={translate('SCREEN_PLAYER_FWD10_BUTTON')}
                    ref={setControlRef(PlayerControl.JUMP_FORWARD)}
                    onClick={() => handleJump('forward')}
                >
                    <PlayerButton data-test-id={'player-skip-forward-button'}>
                        <JumpButton
                            jumpValue={forwardJumpValue}
                            icon={<StyledSVGInline svg={icons.iconJumpForwardStrong} />}
                            isForward={true}
                            totalJumpValue={totalJumpValue}
                        />
                    </PlayerButton>
                </ControlButton>
            </>
        );
    };

    const embeddedCenterControls = () => {
        if (isScrubberValueShown) {
            return null;
        }
        return (
            <>
                <ControlButton
                    key={PlayerControl.JUMP_BACK}
                    render={controls.includes(PlayerControl.JUMP_BACK)}
                    isEnabled={!disabledControls.includes(PlayerControl.JUMP_BACK)}
                    label={translate('SCREEN_PLAYER_BWD10_BUTTON')}
                    ref={setControlRef(PlayerControl.JUMP_BACK)}
                    onClick={() => handleJump('backward')}
                >
                    <PlayerButton data-test-id={'player-skip-back-button'}>
                        <JumpButton
                            jumpValue={backwardJumpValue}
                            icon={<StyledSVGInline svg={icons.iconJumpBackwardStrong} />}
                            isForward={false}
                            totalJumpValue={totalJumpValue}
                        />
                    </PlayerButton>
                </ControlButton>
                ,
                <ControlButton
                    key={PlayerControl.PLAY_PAUSE}
                    label={translate(isPlaying ? 'SCREEN_PLAYER_PAUSE_BUTTON' : 'SCREEN_PLAYER_PLAY_BUTTON')}
                    invisible={!controls.includes(PlayerControl.PLAY_PAUSE)}
                    isEnabled={!disabledControls.includes(PlayerControl.PLAY_PAUSE)}
                    onClick={onPlayPauseClicked}
                >
                    <PlayerButton>
                        <StyledSVGInline svg={isPlaying ? icons.pauseIconSimple : icons.playIconSimple} />
                    </PlayerButton>
                </ControlButton>
                <ControlButton
                    key={PlayerControl.JUMP_FORWARD}
                    render={controls.includes(PlayerControl.JUMP_FORWARD)}
                    isEnabled={!disabledControls.includes(PlayerControl.JUMP_FORWARD)}
                    label={translate('SCREEN_PLAYER_FWD10_BUTTON')}
                    ref={setControlRef(PlayerControl.JUMP_FORWARD)}
                    onClick={() => handleJump('forward')}
                >
                    <PlayerButton data-test-id={'player-skip-forward-button'}>
                        <JumpButton
                            jumpValue={forwardJumpValue}
                            icon={<StyledSVGInline svg={icons.iconJumpForwardStrong} />}
                            isForward={true}
                            totalJumpValue={totalJumpValue}
                        />
                    </PlayerButton>
                </ControlButton>
            </>
        );
    };

    const DesktopControls = () => {
        const leftButtons = [
            <ControlButton
                key={PlayerControl.PLAY_PAUSE}
                render={controls.includes(PlayerControl.PLAY_PAUSE)}
                label={translate(isPlaying ? 'SCREEN_PLAYER_PAUSE_BUTTON' : 'SCREEN_PLAYER_PLAY_BUTTON')}
                isEnabled={!disabledControls.includes(PlayerControl.PLAY_PAUSE)}
                onClick={onPlayPauseClicked}
                ref={setControlRef(PlayerControl.PLAY_PAUSE)}
            >
                <PlayerButton data-test-id={'player-play-pause-button'}>
                    <StyledSVGInline svg={isPlaying ? icons.pauseIconSimple : icons.playIconSimple} />
                </PlayerButton>
            </ControlButton>,
            <ControlButton
                key={PlayerControl.JUMP_BACK}
                render={controls.includes(PlayerControl.JUMP_BACK)}
                isEnabled={!disabledControls.includes(PlayerControl.JUMP_BACK)}
                label={translate('SCREEN_PLAYER_BWD10_BUTTON')}
                ref={setControlRef(PlayerControl.JUMP_BACK)}
                onClick={() => handleJump('backward')}
            >
                <PlayerButton data-test-id={'player-skip-back-button'}>
                    <JumpButton
                        jumpValue={backwardJumpValue}
                        icon={<StyledSVGInline svg={icons.iconJumpBackwardStrong} />}
                        isForward={false}
                        totalJumpValue={totalJumpValue}
                    />
                </PlayerButton>
            </ControlButton>,
            <ControlButton
                key={PlayerControl.JUMP_FORWARD}
                render={controls.includes(PlayerControl.JUMP_FORWARD)}
                isEnabled={!disabledControls.includes(PlayerControl.JUMP_FORWARD)}
                label={translate('SCREEN_PLAYER_FWD10_BUTTON')}
                ref={setControlRef(PlayerControl.JUMP_FORWARD)}
                onClick={() => handleJump('forward')}
            >
                <PlayerButton data-test-id={'player-skip-forward-button'}>
                    <JumpButton
                        jumpValue={forwardJumpValue}
                        icon={<StyledSVGInline svg={icons.iconJumpForwardStrong} />}
                        isForward={true}
                        totalJumpValue={totalJumpValue}
                    />
                </PlayerButton>
            </ControlButton>,
            <ControlButton
                key={PlayerControl.GO_TO_LIVE}
                render={controls.includes(PlayerControl.GO_TO_LIVE)}
                isEnabled={!disabledControls.includes(PlayerControl.GO_TO_LIVE)}
                label={translate('SCREEN_PLAYER_JUMP_TO_LIVE_BUTTON')}
                onClick={onJumpToLiveClicked}
                ref={setControlRef(PlayerControl.GO_TO_LIVE)}
            >
                <PlayerButton>
                    <StyledSVGInline svg={icons.icoGoToLiveProgram} />
                </PlayerButton>
            </ControlButton>,
            <VolumeControlButton key="volume-control-button">
                <ControlButton
                    key={PlayerControl.VOLUME}
                    render={controls.includes(PlayerControl.VOLUME)}
                    label={translate('SCREEN_PLAYER_VOLUME_BUTTON')}
                    isEnabled={true}
                    ref={setControlRef(PlayerControl.VOLUME)}
                >
                    <PlayerButton
                        onClick={onVolumeClicked}
                        data-test-id={'player-volume-button'}
                        className={translate('SCREEN_PLAYER_VOLUME_BUTTON')}
                    >
                        <StyledSVGInline svg={getVolumeIcon()} />
                    </PlayerButton>
                    <VolumeControlHoveringWrapper>
                        <VolumeControl onVolumeInput={onVolumeInput} onVolumeBarCreated={onVolumeBarCreated} />
                    </VolumeControlHoveringWrapper>
                </ControlButton>
            </VolumeControlButton>,
        ];

        const rightButtons = [
            <ControlButton
                key={PlayerControl.NEXT_ASSET}
                render={controls.includes(PlayerControl.NEXT_ASSET) && !!nextSeriesEpisode}
                label={translate('NEXT_EPISODE_BUTTON')}
                isEnabled={!disabledControls.includes(PlayerControl.NEXT_ASSET)}
                ref={setControlRef(PlayerControl.NEXT_ASSET)}
                onClick={() => playSeriesEpisode(nextSeriesEpisode)}
            >
                <PlayerButton data-test-id={'player-next-episode-button'}>
                    <StyledSVGInline svg={icons.nextAssetIcon} />
                </PlayerButton>
            </ControlButton>,
            <ControlButton
                key={PlayerControl.EPISODES}
                render={controls.includes(PlayerControl.EPISODES)}
                label={translate('SCREEN_PLAYER_EPISODES_BUTTON')}
                isEnabled={!disabledControls.includes(PlayerControl.EPISODES)}
                ref={setControlRef(PlayerControl.EPISODES)}
                asContainer
            >
                <PlayerButton data-test-id={'player-episodes-button'}>{actionPopup?.[PlayerControl.EPISODES]}</PlayerButton>
            </ControlButton>,
            <ControlButton
                key={PlayerControl.INFO}
                render={controls.includes(PlayerControl.INFO)}
                label={translate('SCREEN_PLAYER_INFO_BUTTON')}
                isEnabled={!disabledControls.includes(PlayerControl.INFO)}
                onClick={openOverlay}
                ref={setControlRef(PlayerControl.INFO)}
            >
                <PlayerButton className={'info-button'}>
                    <StyledSVGInline svg={icons.infoIcon} />
                </PlayerButton>
            </ControlButton>,
            <ControlButton
                key={PlayerControl.SUBTITLE}
                render={!forceExcludedControls.includes(PlayerControl.SUBTITLE) && controls.includes(PlayerControl.SUBTITLE)}
                label={translate('SCREEN_PLAYER_SETTINGS_BUTTON')}
                isEnabled={!disabledControls.includes(PlayerControl.SUBTITLE)}
                ref={setControlRef(PlayerControl.SUBTITLE)}
                asContainer
            >
                <PlayerButton data-test-id={'player-settings-button'}>{actionPopup?.[PlayerControl.SUBTITLE]}</PlayerButton>
            </ControlButton>,
        ];

        return (
            <>
                <FooterButtonsGroup>{leftButtons}</FooterButtonsGroup>
                <FooterButtonsGroup>{rightButtons}</FooterButtonsGroup>
            </>
        );
    };

    const MobileTabletControls = () => {
        const orderedButtons = [
            <ControlButton
                key={PlayerControl.GO_TO_LIVE}
                render={controls.includes(PlayerControl.GO_TO_LIVE)}
                isEnabled={!disabledControls.includes(PlayerControl.GO_TO_LIVE)}
                label={translate('SCREEN_PLAYER_JUMP_TO_LIVE_BUTTON')}
                onClick={onJumpToLiveClicked}
            >
                <PlayerButton>
                    <StyledSVGInline svg={icons.icoGoToLiveProgram} />
                </PlayerButton>
            </ControlButton>,
            <ControlButton
                key={PlayerControl.NEXT_ASSET}
                render={controls.includes(PlayerControl.NEXT_ASSET) && !!nextSeriesEpisode}
                isEnabled={!disabledControls.includes(PlayerControl.NEXT_ASSET)}
                onClick={() => playSeriesEpisode(nextSeriesEpisode)}
                label={translate('NEXT_EPISODE_BUTTON')}
            >
                <PlayerButton data-test-id={'player-next-episode-button'}>
                    <StyledSVGInline svg={icons.nextAssetIcon} />
                </PlayerButton>
            </ControlButton>,
            <ControlButton
                key={PlayerControl.EPISODES}
                render={controls.includes(PlayerControl.EPISODES)}
                isEnabled={!disabledControls.includes(PlayerControl.EPISODES)}
                onClick={() => setActiveControlGroup(ControlGroup.EPISODES)}
                expanded={activeControlGroup === ControlGroup.EPISODES}
                label={translate('SCREEN_PLAYER_EPISODES_BUTTON')}
            >
                <PlayerButton data-test-id={'player-episodes-button'}>
                    <StyledSVGInline svg={icons.moreEpisodes} />
                </PlayerButton>
            </ControlButton>,
            <ControlButton
                key={PlayerControl.SUBTITLE}
                render={!forceExcludedControls.includes(PlayerControl.SUBTITLE) && controls.includes(PlayerControl.SUBTITLE)}
                isEnabled={!disabledControls.includes(PlayerControl.SUBTITLE)}
                onClick={() => setActiveControlGroup(ControlGroup.AUDIO_SUBTITLE)}
                expanded={activeControlGroup === ControlGroup.AUDIO_SUBTITLE}
                label={translate('SCREEN_PLAYER_SETTINGS_BUTTON')}
            >
                <PlayerButton>
                    <StyledSVGInline svg={icons.icoSettings} />
                </PlayerButton>
            </ControlButton>,
        ];

        return (
            <FooterButtonsContainer>
                <ButtonsInnerContainer>{orderedButtons}</ButtonsInnerContainer>
            </FooterButtonsContainer>
        );
    };

    const ResponsiveFooterButtons = (ControlsComponent, isSplit = false) => (
        <FooterButtonsContainer isSplit={isSplit}>
            <ButtonsInnerContainer isSplit={isSplit}>{ControlsComponent()}</ButtonsInnerContainer>
        </FooterButtonsContainer>
    );

    const onExtraControlsHovered = () => {
        clearTimeout(extraControlsLeaveTimeout.current);
        extraControlsLeaveTimeout.current = window.setTimeout(() => {
            if (activeControlGroup === ControlGroup.BASE && !isMobileDevice && extraControls != null) {
                setActiveControlGroup(ControlGroup.EXTRA);
            }
        }, 250);
    };

    const closeExtraControls = () => {
        setActiveControlGroup(ControlGroup.BASE);
    };

    const onExtraControlsLeave = () => {
        clearTimeout(extraControlsLeaveTimeout.current);
        extraControlsLeaveTimeout.current = window.setTimeout(() => {
            closeExtraControls();
        }, 500);
    };

    const onSwipeUp = () => {
        if (activeControlGroup === ControlGroup.BASE && extraControls != null) {
            setActiveControlGroup(ControlGroup.EXTRA);
        }
    };

    const onSwipeDown = () => {
        closeExtraControls();
    };

    const swipeHandlers = useSwipeable({
        onSwipedUp: onSwipeUp,
        onSwipedDown: onSwipeDown,
    });

    const getThumbnailOnHover = (e) => {
        const scrubberRect = e.target.getBoundingClientRect();
        const cursorX = e.clientX - scrubberRect.left;
        const thumbnailWidth = thumbnail.current.offsetWidth;
        const thumbnailCenterOffset = thumbnailWidth / 2;

        const streamDuration = getDuration();
        const scrubberWidth = scrubberRect.width;
        const hoverValue = (cursorX / scrubberWidth) * streamDuration;
        const thumbnailPosition = calculateThumbnailPosition(cursorX, scrubberWidth, thumbnailCenterOffset, thumbnailWidth);

        getDataForThumbnail(hoverValue);

        if (availableThumbnail.current) {
            toggleRefDisplay(thumbnail, true);
        }

        hoverLine.current.style.width = `${cursorX}px`;
        thumbnail.current.style.left = `${thumbnailPosition}px`;

        if (scrubberValue.current?.style) {
            scrubberValue.current.innerHTML = getSeekTimeLabel(hoverValue);
            scrubberValue.current.style.left = `${thumbnailPosition + thumbnailCenterOffset}px`;
            toggleRefDisplay(scrubberValue, true);
        }
    };

    const clearThumbnailOnHover = () => {
        hoverLine.current.style.width = '0px';
        toggleRefDisplay(thumbnail, false);

        if (!isSeekingRef.current) {
            toggleRefDisplay(scrubberValue, false);
        }
    };

    useEffect(() => {
        if (volumeBar.current && volumeValue.current) {
            const displayValue = isMuted ? 0 : volume;

            volumeBar.current.value = displayValue;
            volumeValue.current.style.width = `${displayValue * 100}%`;
            updateVolumeBarValueText(volumeBar.current, displayValue);
        }
    }, [volume, isMuted, volumeBar.current, volumeValue.current]);

    useEffect(() => {
        if (showThumbnail) {
            availableThumbnail.current = true;
            getDataForThumbnail(0);
        }
    }, [showThumbnail]);

    useEffect(() => {
        isLoaded.current = loaded;
        if (!isSeekingRef.current) {
            if (progressUpdateIntervalId.current) {
                clearInterval(progressUpdateIntervalId.current);
            }
            progressUpdateIntervalId.current = setInterval(updateTimeAndSeekRange, PROGRESS_UPDATE_INTERVAL);
        }
    }, [title, loaded, playingMode]);

    useEffect(() => {
        // TBH I don't know if this is actually doing anything... looks like it is tho :/ why did I write this? idk
        if (isPlaying) {
            progressUpdateIntervalId.current = setInterval(updateTimeAndSeekRange, PROGRESS_UPDATE_INTERVAL);
        } else {
            clearInterval(progressUpdateIntervalId.current);
        }
    }, [isPlaying]);

    useEffect(
        () => () => {
            clearInterval(progressUpdateIntervalId.current);
            clearTimeout(extraControlsLeaveTimeout.current);
            clearTimeout(surfaceClickTimeout.current);
        },
        []
    );

    const handlePlayerKeyboardControl = (event: KeyboardEvent) => {
        const target = event.target instanceof Element ? event.target : null;
        const isArrowKey = ARROW_KEYS.includes(event.code as PlayerKeyboardControl);

        if (target && isTextEntry(target)) return;

        if (isArrowKey && target) {
            if (target === scrubber.current) {
                event.preventDefault();
            } else if (target.closest(ARROW_KEY_WIDGETS)) {
                return;
            }
        }

        if (event.code === PlayerKeyboardControl.SPACE && target?.closest(SPACE_ACTIVATED)) return;

        if (event.repeat) return;

        switch (event.code) {
            case PlayerKeyboardControl.SPACE:
                if (!isEmbedded) {
                    event.preventDefault();
                    setPlayingState(!playing.current);
                    triggerOverlay(!playing.current ? 'pause' : 'play');
                }
                break;
            case PlayerKeyboardControl.LEFTARROW:
                if (!disabledControls.includes(PlayerControl.JUMP_BACK)) {
                    handleJump('backward');
                }
                break;
            case PlayerKeyboardControl.RIGHTARROW:
                if (!disabledControls.includes(PlayerControl.JUMP_FORWARD)) {
                    handleJump('forward');
                }
                break;
            case PlayerKeyboardControl.F:
                if (!isEmbedded) {
                    toggleFullScreen();
                }
                break;
            case PlayerKeyboardControl.M:
                if (!isEmbedded) {
                    toggleMute();
                    triggerOverlay(isMutedRef.current ? 'mute' : 'unmute');
                }
                break;
            case PlayerKeyboardControl.UPARROW:
                if (!isEmbedded) {
                    const iconUp = adjustVolume(true);
                    triggerOverlay(iconUp);
                }
                break;
            case PlayerKeyboardControl.DOWNARROW:
                if (!isEmbedded) {
                    const iconDown = adjustVolume(false);
                    triggerOverlay(iconDown);
                }
                break;
            default:
                break;
        }
    };

    const closeOpenPopup = (): boolean => {
        const popups = {
            [ControlGroup.EPISODES]: { control: PlayerControl.EPISODES, event: PLAYER_EPISODES_DROPDOWN_EVENT },
            [ControlGroup.AUDIO_SUBTITLE]: { control: PlayerControl.SUBTITLE, event: PLAYER_SETTINGS_DROPDOWN_EVENT },
        };
        const popup = popups[activeControlGroup];

        if (!popup) return false;

        if (isDesktop()) {
            document.dispatchEvent(new Event(popup.event));
            focusControl(popup.control);
        } else {
            setActiveControlGroup(ControlGroup.BASE);
        }

        return true;
    };

    useKeyboardShortcuts({
        escape: () => {
            if (isOverlayActive || closeOpenPopup()) {
                return;
            }

            onPlayerCloseClicked(true);
        },
    });

    useEffect(() => {
        const orientationChangeHandler = () => {
            window.setTimeout(() => {
                if (isOrientationLandscape() && isEmbedded && isMobileDevice && asset) {
                    navigate(getLivePlayerURL((asset as LivePlayerAssetInfo).channelId));
                }
            }, 200);
        };

        window.addEventListener('keydown', handlePlayerKeyboardControl);
        window.addEventListener('orientationchange', orientationChangeHandler);
        if (!isEmbedded) {
            fullScreenContainerRef.current?.addEventListener('dblclick', onPlayerSurfaceDoubleClicked);
        }

        return () => {
            window.removeEventListener('keydown', handlePlayerKeyboardControl);
            fullScreenContainerRef.current?.removeEventListener('dblclick', onPlayerSurfaceDoubleClicked);
            window.removeEventListener('orientationchange', orientationChangeHandler);
        };
    }, [isEmbedded, isMobileDevice, asset]);

    useEffect(() => {
        playing.current = isPlaying;
    }, [isPlaying]);

    useEffect(() => {
        isMutedRef.current = isMuted;
    }, [isMuted]);

    useEffect(() => {
        if (!shouldHideLayout) {
            setIsHidingViaButton(false);
        }
    }, [shouldHideLayout]);

    const onHideControlsClicked = () => {
        setIsHidingViaButton(true);
        hideLayoutNow();
        controlsContainerRef.current?.focus({ preventScroll: true });
    };

    const hideControlsButton = !castConnected && (
        <HideControlsButton
            type="button"
            onClick={onHideControlsClicked}
            $keepVisible={isHidingViaButton && shouldHideLayout}
            data-test-id={'player-hide-controls-button'}
        >
            <StyledSVGInline svg={icons.eyeClosedIcon} />
            {translate('SCREEN_PLAYER_HIDE_CONTROLS')}
        </HideControlsButton>
    );

    return (
        <>
            {!isEmbedded && displayTitle && (
                <PageHeading srOnly tabIndex={-1} ref={headingRef}>
                    {displayTitle}
                </PageHeading>
            )}
            {isOverlayActive && (
                <DetailMoreModal
                    modalTitle={title}
                    modalText={description}
                    showMoreMetadataTitle={getMetaData(moreOverlayMetaData?.firstLine)}
                    showMoreMetadataContent={getMetaData(moreOverlayMetaData?.secondLine)}
                    metadata={getMetaData(metaData)}
                    cast={cast?.filter((person) => person.role === PersonRole.ACTOR)}
                    directors={cast?.filter((person) => person.role === PersonRole.DIRECTOR)}
                    original={originalLanguages}
                    caption={captionLanguages}
                    closeOverlay={() => closeOverlay()}
                />
            )}
            <PlayerControls
                tabIndex={-1}
                isHidden={shouldHideLayout}
                activeControlGroup={activeControlGroup}
                ref={(ref) => {
                    controlsContainerRef.current = ref;
                    onControlsRef(ref);
                }}
            >
                <>
                    <FullScreenContainer ref={fullScreenContainerRef} onClick={onPlayerSurfaceClicked} />
                    {!isEmbedded && (
                        <HeaderControls key={'desktop-header-controls'}>
                            <HeaderControlsInnerContainer>
                                {hideControlsButton}
                                <ControlButton
                                    key={PlayerControl.CAST}
                                    render={!forceExcludedControls.includes(PlayerControl.CAST) && controls.includes(PlayerControl.CAST)}
                                    label={translate('SCREEN_PLAYER_CAST_BUTTON')}
                                    isEnabled={!disabledControls.includes(PlayerControl.CAST)}
                                    onClick={onCastClicked}
                                    pressed={castConnected}
                                    ref={setControlRef(PlayerControl.CAST)}
                                    isHeaderControl={true}
                                >
                                    <PlayerButton>
                                        <StyledSVGInline
                                            data-test-id={'player-cast-button'}
                                            svg={castConnected ? icons.castConnectedIcon : icons.castIcon}
                                        />
                                    </PlayerButton>
                                </ControlButton>
                                {!playerError && !castConnected && (
                                    <ControlButton
                                        key={PlayerControl.MINIMIZE}
                                        label={translate('SCREEN_PLAYER_MINIMIZE_BUTTON')}
                                        isEnabled={!disabledControls.includes(PlayerControl.MINIMIZE)}
                                        onClick={onPlayerCloseClicked}
                                        ref={setControlRef(PlayerControl.MINIMIZE)}
                                        isHeaderControl={true}
                                    >
                                        <MinimizeButton data-test-id={'player-back-button'}>
                                            <StyledSVGInline svg={icons.icoMinimize} />
                                        </MinimizeButton>
                                    </ControlButton>
                                )}
                                {!playerError && isDesktop() && (
                                    <ControlButton
                                        key={PlayerControl.FULL_SCREEN}
                                        render={controls.includes(PlayerControl.FULL_SCREEN)}
                                        label={translate(
                                            isFullScreen ? 'SCREEN_PLAYER_EXIT_FULLSCREEN_BUTTON' : 'SCREEN_PLAYER_FULLSCREEN_BUTTON'
                                        )}
                                        isEnabled={!disabledControls.includes(PlayerControl.FULL_SCREEN)}
                                        onClick={onFullScreenToggleClicked}
                                        ref={setControlRef(PlayerControl.FULL_SCREEN)}
                                        isHeaderControl={true}
                                    >
                                        <PlayerButton data-test-id={'player-fullscreen-button'} key={'fullscreen'}>
                                            <StyledSVGInline
                                                data-test-id={isFullScreen ? 'player-fullscreen-icon-true' : 'player-fullscreen-icon-false'}
                                                svg={isFullScreen ? icons.shrinkScreenIcon : icons.fullScreenIcon}
                                            />
                                        </PlayerButton>
                                    </ControlButton>
                                )}
                                <ControlButton
                                    key={PlayerControl.CLOSE}
                                    label={translate('CLOSE_BUTTON')}
                                    isEnabled={!disabledControls.includes(PlayerControl.CLOSE)}
                                    onClick={() => {
                                        onPlayerCloseClicked(true);
                                    }}
                                    ref={setControlRef(PlayerControl.CLOSE)}
                                    isHeaderControl={true}
                                >
                                    <CloseButton>
                                        <StyledSVGInline svg={icons.closeIcon} />
                                    </CloseButton>
                                </ControlButton>
                            </HeaderControlsInnerContainer>
                        </HeaderControls>
                    )}

                    {isEmbedded && (
                        <HeaderEmbeddedControls>
                            {hideControlsButton}
                            <VolumeEmbeddedContainer>
                                <ControlButton
                                    key={PlayerControl.VOLUME}
                                    render={controls.includes(PlayerControl.VOLUME)}
                                    label={translate('SCREEN_PLAYER_VOLUME_BUTTON')}
                                    isEnabled={true}
                                    ref={setControlRef(PlayerControl.VOLUME)}
                                >
                                    <VolumeEmbeddedWrapper
                                        onMouseEnter={() => setIsVolumeHovered(true)}
                                        onMouseLeave={() => setIsVolumeHovered(false)}
                                    >
                                        <VolumeControlButton>
                                            <PlayerButton
                                                data-test-id={'player-volume-button'}
                                                className={translate('SCREEN_PLAYER_VOLUME_BUTTON')}
                                                onClick={onVolumeClicked}
                                            >
                                                <StyledSVGInline svg={getVolumeIcon()} />
                                            </PlayerButton>
                                            <VolumeControlEmbedded isVisible={isVolumeHovered}>
                                                <VolumeControl onVolumeInput={onVolumeInput} onVolumeBarCreated={onVolumeBarCreated} />
                                            </VolumeControlEmbedded>
                                        </VolumeControlButton>
                                    </VolumeEmbeddedWrapper>
                                </ControlButton>
                            </VolumeEmbeddedContainer>
                            {!playerError && !isMobile() && !castConnected && (
                                <MinimizeButton
                                    data-test-id={'player-minimize-button'}
                                    onClick={() => {
                                        setIsEmbedded(false);
                                        switchToMiniPlayer({
                                            params: {
                                                channelId: (asset as LivePlayerAssetInfo).channel_id,
                                            },
                                            type: PlayingAssetType[asset.use_case],
                                        });
                                    }}
                                >
                                    <StyledSVGInline svg={icons.icoMinimize} />
                                </MinimizeButton>
                            )}
                            {!isIphone() && !playerError && (
                                <ControlButton
                                    key={PlayerControl.FULL_SCREEN}
                                    render={controls.includes(PlayerControl.FULL_SCREEN)}
                                    label={translate('SCREEN_PLAYER_FULLSCREEN_BUTTON')}
                                    isEnabled={!disabledControls.includes(PlayerControl.FULL_SCREEN)}
                                    className={'fullscren-btn'}
                                    onClick={() => {
                                        const liveURL = getLivePlayerURL((asset as LivePlayerAssetInfo).channelId);
                                        const separator = liveURL.includes('?') ? '&' : '?';
                                        navigate(`${liveURL}${separator}live=true`);
                                    }}
                                    ref={setControlRef(PlayerControl.FULL_SCREEN)}
                                >
                                    <PlayerButton>
                                        <FullScreenToggleIcon svg={isFullScreen ? icons.shrinkScreenIcon : icons.fullScreenIcon} />
                                    </PlayerButton>
                                </ControlButton>
                            )}
                        </HeaderEmbeddedControls>
                    )}

                    {!isEmbedded && !isDesktop() && !playerError && (
                        <MiddleButtonsContainer key={'mobile-middle-controls'}>{centerControls()}</MiddleButtonsContainer>
                    )}

                    {isEmbedded && !playerError && (
                        <EmbeddedMiddleButtonsContainer key={'embedded-middle-controls'}>
                            {embeddedCenterControls()}
                        </EmbeddedMiddleButtonsContainer>
                    )}

                    {!isScrubberValueShown && isEmbedded && !playerError && (
                        <EmbeddedMetaDataContainer>
                            <EmbeddedChanelInfoContainer>{logo}</EmbeddedChanelInfoContainer>
                            {displayTitle && <EmbeddedTitle data-test-id={'player-content-title'}>{displayTitle}</EmbeddedTitle>}
                        </EmbeddedMetaDataContainer>
                    )}

                    {!isScrubberValueShown && !isEmbedded && (
                        <MetaDataContainerMobile>
                            {logo && (
                                <UpperContainer>
                                    <ChanelInfoContainer>{logo}</ChanelInfoContainer>
                                </UpperContainer>
                            )}
                            {subtitle && <Subtitle data-test-id={'player-content-subtitle'}>{subtitle}</Subtitle>}
                            <PlayerMetaData>
                                {displayTitle && (
                                    <Title data-test-id={'player-content-title'} onClick={openOverlay}>
                                        {displayTitle}
                                        <MoreInfoArrow>
                                            {!isMobile() ? <StyledSVGInline svg={icons.rightArrowIconSmall} /> : null}
                                        </MoreInfoArrow>
                                    </Title>
                                )}
                                {subtitle && <MetadataSubtitle data-test-id={'player-content-subtitle'}>{subtitle}</MetadataSubtitle>}
                            </PlayerMetaData>
                        </MetaDataContainerMobile>
                    )}

                    <div {...swipeHandlers}>
                        {!isScrubberValueShown && !isEmbedded && (
                            <MetaDataContainer>
                                {displayLogo && <ChanelInfoContainer>{displayLogo}</ChanelInfoContainer>}
                                <MetaDataTextColumn>
                                    {displayTitle && <Title data-test-id={'player-content-title'}>{displayTitle}</Title>}
                                    {subtitle && <Subtitle data-test-id={'player-content-subtitle'}>{subtitle}</Subtitle>}
                                </MetaDataTextColumn>
                            </MetaDataContainer>
                        )}
                        <FooterControls
                            isEmbedded={isEmbedded}
                            data-test-id={'footer-controls'}
                            hasExtraControls={extraControls != null}
                            activeControlGroup={activeControlGroup}
                            withAnimation={true}
                            hasOpacity={
                                loading || !loaded || (!isSeeking && isBuffering && guardedSessionStorage.getItem('e_smv') !== 'true')
                            } // jank
                            isMobile={isMobile()}
                        >
                            {castConnected && deviceName && (
                                <CastingLabel>{translate('FEATURE_CAST_SENDER_HINT', { receiver_name: deviceName })}</CastingLabel>
                            )}
                            <SeekBarHolder isEmbedded={isEmbedded}>
                                <SeekBar
                                    enabled={seekEnabled && !playerError}
                                    onSeekInput={onSeekInput}
                                    onSeekStart={onSeekStart}
                                    onSeekEnd={onSeekEnd}
                                    onSeekBarCreated={onSeekBarCreated}
                                    onMouseHover={getThumbnailOnHover}
                                    onMouseLeave={clearThumbnailOnHover}
                                    ref={setControlRef(PlayerControl.SEEKBAR)}
                                />
                            </SeekBarHolder>

                            {!isTablet() &&
                                !isMobile() &&
                                !isMobileLandscape() &&
                                !isEmbedded &&
                                !playerError &&
                                ResponsiveFooterButtons(DesktopControls, true)}

                            {(isTablet() || isMobileLandscape() || isMobile()) &&
                                !isEmbedded &&
                                !playerError &&
                                ResponsiveFooterButtons(MobileTabletControls)}

                            {extraControls != null && (
                                <ExtraControlsWrapper onMouseEnter={onExtraControlsHovered} onMouseLeave={onExtraControlsLeave}>
                                    {activeControlGroup === ControlGroup.EXTRA && (
                                        <CloseExtraControls onClick={closeExtraControls}>
                                            <StyledSVGInline svg={icons.downArrowIcon} />
                                        </CloseExtraControls>
                                    )}
                                    {extraControls}
                                </ExtraControlsWrapper>
                            )}
                        </FooterControls>
                    </div>
                </>
            </PlayerControls>
            <KeyboardShortcutOverlay
                iconName={overlayIcon}
                overlayTrigger={overlayTrigger}
                isEnabled={isPlayerReady}
                forwardJumpValue={forwardJumpValue}
                backwardJumpValue={backwardJumpValue}
            />

            {(isMobileLandscape() || isMobile() || isTablet()) && <>{mobileOverlayActionHandlers}</>}
            <BufferingIndicator buffering={(loading || !loaded || isBuffering) && !playerError} />
        </>
    );
};
